/**
 * CR309: Hook for fetching assets by type
 * 
 * Reusable hook to load assets from Asset Studio filtered by type.
 * Used by AssetPickerModal and other components.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AppAsset, AssetType } from "@/lib/asset-studio/types";

interface UseAssetsByTypeOptions {
  /** Asset type to filter by */
  type: AssetType;
  /** Only fetch active assets (default: true) */
  activeOnly?: boolean;
  /** Enable the query (default: true) */
  enabled?: boolean;
}

interface UseAssetsByTypeResult {
  assets: AppAsset[];
  loading: boolean;
  error: Error | null;
  hasAssets: boolean;
  defaultAsset: AppAsset | null;
  refetch: () => void;
}

export function useAssetsByType({
  type,
  activeOnly = true,
  enabled = true,
}: UseAssetsByTypeOptions): UseAssetsByTypeResult {
  const query = useQuery({
    queryKey: ["app-assets", type, activeOnly],
    queryFn: async () => {
      let q = supabase
        .from("app_assets")
        .select("*")
        .eq("type", type)
        .order("updated_at", { ascending: false });

      if (activeOnly) {
        q = q.eq("status", "active");
      }

      const { data, error } = await q;

      if (error) {
        console.error(`[useAssetsByType] Error fetching ${type} assets:`, error);
        throw error;
      }

      return (data || []) as AppAsset[];
    },
    enabled,
    staleTime: 30_000, // Cache for 30 seconds
  });

  // Find default-tagged asset
  const defaultAsset =
    query.data?.find((a) =>
      a.tags?.some((t) => t.toLowerCase() === "default")
    ) ?? null;

  return {
    assets: query.data || [],
    loading: query.isLoading,
    error: query.error as Error | null,
    hasAssets: (query.data?.length ?? 0) > 0,
    defaultAsset,
    refetch: query.refetch,
  };
}
